import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Flex, Spin, Typography } from 'antd';
import { Button, Popover } from '@bettercomp/ui-components';
import { selectGroupedBackgroundTasks } from 'redux/backgroundTasks/selectors';
import { getJobTitleByType } from 'redux/backgroundTasks';
import { formatDate, YEAR_DATE_TIME_WITH_SECONDS_FORMAT } from 'utils/date';
import { AsyncJobType } from 'types';
import { ClockCircleOutlined, DownOutlined, SyncOutlined, UpOutlined } from '@ant-design/icons';
import cn from './styles.module.scss';

export const BackgroundTasks = () => {
  const [expanded, setExpanded] = useState<AsyncJobType[]>([]);
  const taskGroups = useSelector(selectGroupedBackgroundTasks);
  const tasksCount = taskGroups.reduce((sum, taskGroup) => {
    return sum + taskGroup.tasks.length;
  }, 0);

  const handleGroupClick = (type: AsyncJobType) => () => {
    if (expanded.includes(type)) {
      setExpanded(expanded.filter(expandedItem => expandedItem !== type));
    } else {
      setExpanded([...expanded, type]);
    }
  };

  return (
    <Popover
      classNames={{ root: cn.popoverRoot, container: cn.popoverContainer }}
      content={() => (
        <div className={cn.tasks}>
          {tasksCount === 0 && (
            <Flex vertical align="center" justify="center" gap={4} className={cn.emptyState}>
              <Flex align="center" justify="center" className={cn.emptyIcon}>
                <ClockCircleOutlined />
              </Flex>
              <Typography.Text>No background work running</Typography.Text>
              <Typography.Text type="secondary" className={cn.emptyDescription}>
                Scheduled jobs, exports and async tasks will show up here while they&apos;re active.
              </Typography.Text>
            </Flex>
          )}
          {taskGroups.map(taskGroup => (
            <div className={cn.taskGroups} key={taskGroup.type}>
              <div className={cn.groupTitle}>
                {getJobTitleByType(taskGroup.type)}
                <div className={cn.actions}>
                  {taskGroup.inProgress && (
                    <Spin size="small" />
                  )}
                  <Badge count={taskGroup.tasks.length} />
                  {expanded.includes(taskGroup.type)
                    ? <Button size="small" type="text" onClick={handleGroupClick(taskGroup.type)} icon={<UpOutlined />} />
                    : <Button size="small" type="text" onClick={handleGroupClick(taskGroup.type)} icon={<DownOutlined />} />
                  }
                </div>
              </div>
              {expanded.includes(taskGroup.type) && taskGroup.tasks.map(task => (
                <div key={task.id} className={cn.task}>
                  <div className={cn.info}>
                    <span className={cn.title}>{getJobTitleByType(task.type)}</span>
                    <span className={cn.date}>{formatDate(task.createdAt, YEAR_DATE_TIME_WITH_SECONDS_FORMAT)}</span>
                  </div>
                  <div className={cn.status}>
                    {task.status === 'inProgress' && (
                      <Spin size="small" />
                    )}
                    <div>{task.status}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      trigger="click"
      placement="bottomRight"
      onOpenChange={(isVisible) => !isVisible && setExpanded([])}
    >
      <Badge count={tasksCount} data-testid="backgroundTasksCount" data-count={tasksCount}>
        <Button
          type="text"
          icon={<SyncOutlined className={cn.taskIcon} />}
          style={{ background: "transparent" }}
        />
      </Badge>
    </Popover>
  );
};
